/**
 * Classifica dei servizi piu' venduti (regole pure, testate in __tests__/topServices.test.ts).
 * Le righe senza servizio collegato (prodotti, nome personalizzato) restano in classifica, cosi' la somma
 * quadra con i ricavi; lo sconto 100% (discounted_amount = 0) conta come sconto.
 */

/** Voce per le righe senza servizio e senza nome personalizzato. */
export const NO_SERVICE_ID = '__no_service__';
export const NO_SERVICE_NAME = 'Altre righe (senza servizio)';

export interface TopServiceLine {
  dealId: string;
  serviceId?: string | null;
  serviceTitle?: string | null;
  categoryName?: string | null;
  categoryColor?: string | null;
  serviceType?: string | null;
  customServiceName?: string | null;
  amount: number;
  discountedAmount?: number | null;
  monthlyDuration?: number | null;
}

export interface TopServiceRow {
  serviceId: string;
  serviceName: string;
  categoryName: string | null;
  categoryColor: string | null;
  serviceType: string;
  totalRevenue: number;
  dealsCount: number;
  totalDiscount: number;
  totalAmount: number;
  averagePrice: number;
  averageDiscountRate: number;
  rank: number;
  /** Riga senza servizio collegato: niente elenco delle offerte per servizio. */
  isCustom?: boolean;
}

/** Chiave e nome della voce: servizio, altrimenti nome personalizzato, altrimenti la voce comune. */
function groupOf(line: TopServiceLine): { id: string; name: string; custom: boolean } {
  if (line.serviceId) return { id: line.serviceId, name: line.serviceTitle || 'Servizio', custom: false };
  const custom = (line.customServiceName || '').trim();
  if (custom) return { id: `custom:${custom.toLowerCase()}`, name: custom, custom: true };
  return { id: NO_SERVICE_ID, name: NO_SERVICE_NAME, custom: true };
}

export function aggregateTopServices(lines: TopServiceLine[], limit: number): TopServiceRow[] {
  const map = new Map<string, TopServiceRow>();
  const deals = new Map<string, Set<string>>();

  for (const line of lines) {
    const g = groupOf(line);
    const amount = Number(line.amount) || 0;
    const hasDiscount = line.discountedAmount != null; // lo sconto 100% (0) e' uno sconto
    const revenue = Number(line.discountedAmount ?? amount) * (line.monthlyDuration || 1);
    const discount = hasDiscount ? amount - Number(line.discountedAmount) : 0;

    let row = map.get(g.id);
    if (!row) {
      row = {
        serviceId: g.id,
        serviceName: g.name,
        categoryName: g.custom ? null : line.categoryName || null,
        categoryColor: g.custom ? null : line.categoryColor || null,
        serviceType: g.custom ? 'one_time' : line.serviceType || 'one_time',
        totalRevenue: 0,
        dealsCount: 0,
        totalDiscount: 0,
        totalAmount: 0,
        averagePrice: 0,
        averageDiscountRate: 0,
        rank: 0,
        isCustom: g.custom,
      };
      map.set(g.id, row);
      deals.set(g.id, new Set());
    }
    row.totalRevenue += revenue;
    row.totalDiscount += discount;
    row.totalAmount += amount;
    deals.get(g.id)!.add(line.dealId);
  }

  return Array.from(map.values())
    .map(r => {
      const n = deals.get(r.serviceId)!.size;
      return {
        ...r,
        dealsCount: n,
        averagePrice: n > 0 ? r.totalRevenue / n : 0,
        averageDiscountRate: r.totalAmount > 0 ? (r.totalDiscount / r.totalAmount) * 100 : 0,
      };
    })
    .sort((a, b) => b.totalRevenue - a.totalRevenue)
    .slice(0, limit)
    .map((r, i) => ({ ...r, rank: i + 1 }));
}

/** Sconto medio pesato sull'importo di listino, come la colonna "Sconto medio" di ogni riga. */
export function weightedDiscountRate(rows: Array<{ totalDiscount: number; totalAmount: number }>): number {
  const amount = rows.reduce((s, r) => s + r.totalAmount, 0);
  const discount = rows.reduce((s, r) => s + r.totalDiscount, 0);
  return amount > 0 ? (discount / amount) * 100 : 0;
}
